import React, { useState } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView, Alert, TextInput,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { ProfileStackParamList } from '../../navigation/AppNavigator';
import { useUserStore } from '../../store';
import type { UserProfile } from '../../types';
import { PROFILE_KEY } from '../auth/OnboardingScreen';

type Props = NativeStackScreenProps<ProfileStackParamList, 'Profile'>;

const AGE_GROUPS: UserProfile['ageGroup'][] = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
const GENDERS: Array<{ label: string; value: UserProfile['gender'] }> = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
  { label: 'Prefer not to say', value: 'prefer_not_to_say' },
];

export default function ProfileScreen({ navigation }: Props) {
  const { profile, setProfile } = useUserStore();
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(profile?.displayName ?? '');
  const [ageGroup, setAgeGroup] = useState<UserProfile['ageGroup']>(profile?.ageGroup);
  const [gender, setGender] = useState<UserProfile['gender']>(profile?.gender);

  async function handleSave() {
    try {
      const updated = { ...profile!, displayName, ageGroup, gender };
      await AsyncStorage.setItem(PROFILE_KEY, JSON.stringify(updated));
      setProfile(updated);
      setEditing(false);
    } catch (e: any) {
      Alert.alert('Failed to save', e.message ?? 'Unknown error');
    }
  }

  async function handleResetApp() {
    Alert.alert('Reset App', 'This will delete all your landmarks, runs, and profile. Are you sure?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Reset', style: 'destructive', onPress: async () => {
          await AsyncStorage.clear();
          setProfile(null);
        },
      },
    ]);
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{(profile?.displayName?.[0] ?? '?').toUpperCase()}</Text>
        </View>
        <Text style={styles.email}>{profile?.email}</Text>
      </View>

      {editing ? (
        <View style={styles.section}>
          <Text style={styles.label}>Display Name</Text>
          <TextInput style={styles.input} value={displayName} onChangeText={setDisplayName} />

          <Text style={styles.label}>Age Group</Text>
          <View style={styles.chips}>
            {AGE_GROUPS.map((ag) => (
              <TouchableOpacity
                key={ag}
                style={[styles.chip, ageGroup === ag && styles.chipSelected]}
                onPress={() => setAgeGroup(ageGroup === ag ? undefined : ag)}
              >
                <Text style={[styles.chipText, ageGroup === ag && styles.chipTextSelected]}>{ag}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={styles.label}>Gender</Text>
          <View style={styles.chips}>
            {GENDERS.map((g) => (
              <TouchableOpacity
                key={g.value}
                style={[styles.chip, gender === g.value && styles.chipSelected]}
                onPress={() => setGender(gender === g.value ? undefined : g.value)}
              >
                <Text style={[styles.chipText, gender === g.value && styles.chipTextSelected]}>{g.label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <TouchableOpacity style={styles.button} onPress={handleSave}>
            <Text style={styles.buttonText}>Save Profile</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setEditing(false)} style={styles.cancelBtn}>
            <Text style={styles.cancelText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.section}>
          <InfoRow label="Name" value={profile?.displayName ?? '—'} />
          <InfoRow label="Age Group" value={profile?.ageGroup ?? 'Not set'} />
          <InfoRow label="Gender" value={
            GENDERS.find((g) => g.value === profile?.gender)?.label ?? 'Not set'
          } />
          <InfoRow label="Groups" value={profile?.groups?.join(', ') || 'None'} />

          <TouchableOpacity style={styles.button} onPress={() => setEditing(true)}>
            <Text style={styles.buttonText}>Edit Profile</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.secondaryBtn} onPress={() => navigation.navigate('Settings')}>
            <Text style={styles.secondaryText}>⚙️ Announcement Settings</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.secondaryBtn} onPress={() => navigation.navigate('Logs')}>
            <Text style={styles.secondaryText}>🪵 Activity Logs</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.signOutBtn} onPress={handleResetApp}>
            <Text style={styles.signOutText}>🗑️ Reset App Data</Text>
          </TouchableOpacity>
        </View>
      )}
    </ScrollView>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={infoStyles.row}>
      <Text style={infoStyles.label}>{label}</Text>
      <Text style={infoStyles.value}>{value}</Text>
    </View>
  );
}

const infoStyles = StyleSheet.create({
  row: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 12, borderBottomWidth: 1, borderColor: '#f0f0f0' },
  label: { color: '#888', fontSize: 14 },
  value: { fontWeight: '600', fontSize: 14 },
});

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8f9fa' },
  header: { backgroundColor: '#1a73e8', padding: 32, alignItems: 'center' },
  avatar: { width: 72, height: 72, borderRadius: 36, backgroundColor: '#fff', justifyContent: 'center', alignItems: 'center', marginBottom: 12 },
  avatarText: { fontSize: 32, fontWeight: '700', color: '#1a73e8' },
  email: { color: 'rgba(255,255,255,0.85)', fontSize: 14 },
  section: { backgroundColor: '#fff', margin: 16, borderRadius: 12, padding: 16 },
  label: { fontWeight: '600', fontSize: 13, color: '#555', marginTop: 12, marginBottom: 6 },
  input: { borderWidth: 1, borderColor: '#ccc', borderRadius: 8, padding: 10, fontSize: 15 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: { borderWidth: 1, borderColor: '#ccc', borderRadius: 16, paddingHorizontal: 12, paddingVertical: 6 },
  chipSelected: { backgroundColor: '#1a73e8', borderColor: '#1a73e8' },
  chipText: { fontSize: 13, color: '#333' },
  chipTextSelected: { color: '#fff' },
  button: { backgroundColor: '#1a73e8', borderRadius: 8, padding: 14, alignItems: 'center', marginTop: 20 },
  buttonText: { color: '#fff', fontWeight: '600', fontSize: 15 },
  cancelBtn: { padding: 10, alignItems: 'center' },
  cancelText: { color: '#666' },
  secondaryBtn: { borderWidth: 1, borderColor: '#1a73e8', borderRadius: 8, padding: 14, alignItems: 'center', marginTop: 10 },
  secondaryText: { color: '#1a73e8', fontWeight: '600' },
  signOutBtn: { padding: 14, alignItems: 'center', marginTop: 10 },
  signOutText: { color: '#ea4335', fontWeight: '600' },
});
